import isEqual from "lodash.isequal";

import {
  AnalyzeSitewideRequestBodyType,
  DataType,
  GraphConfig,
  GraphGlobalMetric,
  GraphLegend,
  SitewideInterval,
  TimeSeriesData,
} from "@intelligems/ig-types";
import { SxProps } from "@mui/system";
import { SitewideHolidayBenchmarkingTimeSeries } from "@src/features/analytics/types";
import {
  initialSitewideDashboardFiltersState,
  ISitewideDashboardFilters,
} from "@src/store/types/sitewideDashboardFilters";

import { METRIC_TO_LEGEND_LABEL } from "../utils";

type AnalyticsData = any;

type GraphDataset = {
  name: string;
  [key: string]: string | number;
};

const INTERVAL_TO_READABLE_LABEL: Record<SitewideInterval, string> = {
  "1 DAY": "daily",
  "1 WEEK": "weekly",
  "1 MONTH": "monthly",
};

export const METRIC_TO_DESCRIPTION: Record<string, string> = {
  abandoned_cart_rate:
    "% of sessions who add to cart but do not make a purchase.",
  abandoned_checkout_rate:
    "% of sessions who started checkout but do not make a purchase.",
  add_to_cart_rate: "% of sessions who added a product to cart.",
  aov: "Average Order Value. Net revenue divided by number of orders.",
  average_discount:
    "Total discounts divided by gross revenue. The denominator is total gross revenue. Inclusive of orders with 0 discount.",
  average_units_per_order: "Average Units Per Order",
  bounce_rate:
    "% of sessions who ended their session without navigating to a second page",
  checkout_begin_rate: "% of sessions who started checkout.",
  conversion_rate: "Orders divided by unique site sessions.",
  gross_profit:
    "Product and shipping revenue, net of discounts, returns, COGS, cost of shipping, and transaction fees.",
  gross_profit_per_visitor:
    "Gross profit divided by number of unique site visitors. Gross profit includes product and shipping revenue, net of discounts, returns, COGS, cost of shipping, and transaction fees.",
  median_order_value:
    "The order amount where half of orders are lower and half are higher.",
  net_revenue: "Product and shipping revenue, net of discounts and returns",
  net_revenue_per_visitor:
    "Net revenue divided by number of unique site visitors. Net revenue includes product and shipping revenue, net of discounts.",
  net_shipping_revenue_per_order:
    "Average net revenue per order generated by shipping charges. This is the shipping revenue portion of AOV. Inclusive of orders with 0 ship revenue.",
  pct_orders_with_free_shipping:
    "Percentage of orders with no associated net shipping revenue.",
  viewed_collection_page_rate: "% of sessions that viewed a collection page.",
  viewed_product_page_rate:
    "% of sessions who viewed a product detail page (PDP).",
};

export const METRIC_TO_DATA_TYPE: Record<string, DataType> = {
  abandoned_cart_rate: "percent",
  abandoned_checkout_rate: "percent",
  add_to_cart_rate: "percent",
  aov: "currencyNoDecimal",
  aov_pre_discount: "currencyNoDecimal",
  average_discount: "percent",
  average_units_per_order: "number",
  avg_unit_price: "currencyNoDecimal",
  bounce_rate: "percent",
  checkout_begin_rate: "percent",
  conversion_rate: "percent",
  gross_margin_pct: "percent",
  gross_profit: "currencyNoDecimal",
  gross_profit_per_visitor: "currency",
  index_lift: "percentNoDecimal",
  median_order_value: "currency",
  n_orders: "numberNoDecimal",
  n_visitors: "numberNoDecimal",
  net_product_revenue: "currencyNoDecimal",
  net_revenue_lift: "percentNoDecimal",
  net_revenue: "currencyNoDecimal",
  net_revenue_per_visitor: "currency",
  net_shipping_revenue_per_order: "currency",
  pct_gross_profit_by_audience: "percent",
  pct_net_revenue_by_audience: "percent",
  pct_orders_by_audience: "percent",
  pct_orders_by_unit_quantity: "percent",
  pct_orders_with_free_shipping: "percentNoDecimal",
  pct_profit_by_unit_quantity: "percent",
  pct_revenue_by_unit_quantity: "percent",
  pct_visitors_by_audience: "percent",
  viewed_collection_page_rate: "percent",
  viewed_product_page_rate: "percent",
};

export const METRIC_TO_FORMAT_TYPE: Record<
  string,
  Intl.NumberFormatOptionsStyle
> = {
  abandoned_cart_rate: "percent",
  abandoned_checkout_rate: "percent",
  add_to_cart_rate: "percent",
  aov: "currency",
  aov_pre_discount: "currency",
  average_discount: "percent",
  average_units_per_order: "decimal",
  avg_unit_price: "currency",
  bounce_rate: "percent",
  checkout_begin_rate: "percent",
  conversion_rate: "percent",
  gross_margin_pct: "percent",
  gross_profit: "currency",
  gross_profit_per_visitor: "currency",
  index_lift: "percent",
  median_order_value: "currency",
  n_orders: "unit",
  n_visitors: "unit",
  net_product_revenue: "currency",
  net_revenue_lift: "percent",
  net_revenue: "currency",
  net_revenue_per_visitor: "currency",
  net_shipping_revenue_per_order: "currency",
  pct_gross_profit_by_audience: "percent",
  pct_net_revenue_by_audience: "percent",
  pct_orders_by_audience: "percent",
  pct_orders_by_unit_quantity: "percent",
  pct_orders_with_free_shipping: "percent",
  pct_profit_by_unit_quantity: "percent",
  pct_revenue_by_unit_quantity: "percent",
  pct_visitors_by_audience: "percent",
  viewed_collection_page_rate: "percent",
  viewed_product_page_rate: "percent",
};

const getGlobalMetricLabelWithTimeInterval = (
  metric: string,
  timeInterval: SitewideInterval
) => {
  const intervalLabel = INTERVAL_TO_READABLE_LABEL[timeInterval];

  switch (metric) {
    case "aov":
      return `Avg. ${intervalLabel} order value`;
    case "conversion_rate":
      return `Avg. CVR`;
    case "gross_profit":
      return `Avg. ${intervalLabel} gross profit`;
    case "gross_profit_per_visitor":
      return `Avg. ${intervalLabel} PPV`;
    case "n_orders":
      return `Avg. ${intervalLabel} orders`;
    case "n_visitors":
      return `Avg. ${intervalLabel} visitors`;
    case "net_revenue":
      return `Avg. ${intervalLabel} revenue`;
    case "net_revenue_per_visitor":
      return `Avg. ${intervalLabel} RPV`;
    case "net_shipping_revenue_per_order":
      return `Avg. ${intervalLabel} shipping revenue per order`;
    case "pct_orders_with_free_shipping":
      return `Avg. ${intervalLabel} % orders with free shipping`;
    default:
      return "";
  }
};

export const getGlobalMetrics = (
  globalMetricsData: Record<string, number>,
  graphConfig: GraphConfig,
  timeInterval: SitewideInterval,
  filteredMetricList?: GraphConfig["metrics"]
): GraphGlobalMetric[] => {
  const globalMetrics: GraphGlobalMetric[] = (
    filteredMetricList ?? graphConfig.metrics
  ).map((metric) => {
    return {
      dataType: METRIC_TO_DATA_TYPE[metric],
      label: getGlobalMetricLabelWithTimeInterval(metric, timeInterval),
      value: globalMetricsData[metric],
    };
  });

  return globalMetrics;
};

export const getGraphLegends = (graphConfig: GraphConfig): GraphLegend[] => {
  return graphConfig.metrics.map((metric) => {
    return {
      dataType: METRIC_TO_DATA_TYPE[metric],
      id: metric,
      name: METRIC_TO_LEGEND_LABEL[metric],
    };
  });
};

export const haveSitewideFiltersBeenModified = (
  dashboardFilters: ISitewideDashboardFilters
) => {
  const reducedDashFilters: Partial<AnalyzeSitewideRequestBodyType> = {
    deviceType: dashboardFilters.deviceType,
    visitorType: dashboardFilters.visitorType,
    customerType: dashboardFilters.customerType,
  };
  const reducedDashInitials: Partial<AnalyzeSitewideRequestBodyType> = {
    deviceType: initialSitewideDashboardFiltersState.deviceType,
    visitorType: initialSitewideDashboardFiltersState.visitorType,
    customerType: initialSitewideDashboardFiltersState.customerType,
  };
  return !isEqual(reducedDashFilters, reducedDashInitials);
};

export const selectSitewideAnalyticsData = (
  analyticsData: AnalyticsData,
  graphConfig: GraphConfig
): GraphDataset[] | undefined => {
  if (!analyticsData) return;
  const xAxisKeyFromData = graphConfig["filterMetric"] || "dt";

  const dataset = analyticsData.map((data) => {
    const graphData = graphConfig.metrics.reduce((graphAccumulator, metric) => {
      return {
        ...graphAccumulator,
        [metric]: data[metric],
      };
    }, {});

    return {
      ...graphData,
      xAxisKey: data[xAxisKeyFromData],
    };
  });

  return dataset;
};

const getHolidayBenchmarkingTimeseriesBrand = (
  brandValue: string,
  holidayYear: string,
  type: string
) => {
  const prefix = brandValue.includes("index") ? "index" : "brand";
  return `${prefix}_${holidayYear}_${type}`;
};

type SelectHolidayBenchmarkingTimeseriesDataParams = {
  data: SitewideHolidayBenchmarkingTimeSeries[] | undefined;
  displayActualDate: boolean;
  graphConfig: GraphConfig;
  isBaseline: boolean;
  dataIdsToInclude?: string[]; // This helps not generating graph data for elements not shown on the graph. Doing this prevents chart axis from growing larger for data that is not displayed.
  timeLabels?: Record<number, string>;
};

export const selectHolidayBenchmarkingTimeseriesData = ({
  data,
  displayActualDate,
  graphConfig,
  isBaseline,
  dataIdsToInclude,
  timeLabels,
}: SelectHolidayBenchmarkingTimeseriesDataParams) => {
  if (!data || data.length === 0 || !timeLabels) return undefined;
  const formattedData: TimeSeriesData[] = [];
  const formattedTimeLabels = new Map(); // Use Map to keep keys in order
  for (const key in timeLabels) {
    formattedTimeLabels.set(parseInt(key), timeLabels[key]);
  }
  const dataGroupedBySameFilterMetric = new Map(); // Use Map to keep keys in order
  data.forEach((dataItem) => {
    const currentFilterMetricValue = dataItem[
      graphConfig.filterMetric!
    ] as number;
    const graphKey = getHolidayBenchmarkingTimeseriesBrand(
      dataItem.brand as string,
      dataItem.holiday_year as string,
      dataItem.type as string
    );
    if (dataIdsToInclude && !dataIdsToInclude.includes(graphKey)) return;

    const metric = graphConfig?.metrics?.[0];
    const subMetric = isBaseline ? "pct_of_full_day_baseline" : "value";
    dataGroupedBySameFilterMetric.set(currentFilterMetricValue, {
      ...dataGroupedBySameFilterMetric.get(currentFilterMetricValue),
      [graphKey]: dataItem[metric][subMetric],
    });
  });

  dataGroupedBySameFilterMetric.forEach((dataItem: TimeSeriesData, index) => {
    formattedData.push({
      ...dataItem,
      xAxisKey: displayActualDate ? formattedTimeLabels.get(index) : index,
      label: index,
    });
  });

  return formattedData;
};

export const getGlobalBenchmarkMetrics = (
  globalMetricsData: Record<
    string,
    {
      value: number;
    }
  >,
  graphConfig: GraphConfig,
  isBaseLine: boolean
): GraphGlobalMetric[] => {
  const globalMetrics: GraphGlobalMetric[] = graphConfig.metrics.map(
    (metric) => {
      return {
        dataType: isBaseLine ? "percentNoDecimal" : METRIC_TO_DATA_TYPE[metric],
        label: "",
        value:
          globalMetricsData?.[metric]?.[
            isBaseLine ? "pct_of_full_day_baseline" : "value"
          ],
      };
    }
  );

  return globalMetrics;
};

export const getBenchmarkMetricTitle = (
  metric: string,
  useHourlyTitle: boolean
) => {
  switch (metric) {
    case "net_revenue":
      return useHourlyTitle ? "Running Hourly Revenue" : "Revenue";
    case "n_orders":
      return useHourlyTitle ? "Running Hourly Orders" : "Orders";
    case "avg_unit_price":
      return useHourlyTitle
        ? "Running Average Unit Price"
        : "Average Unit Price";
    default:
      return useHourlyTitle ? "Running Hourly Revenue" : "Revenue";
  }
};

export const getBaselineBenchmarkMetricTitle = (
  metric: string,
  useHourlyTitle: boolean
) => {
  switch (metric) {
    case "net_revenue":
      return useHourlyTitle
        ? "Running Hourly Revenue (as % of typical full day)"
        : "Revenue (as % of typical full day)";
    case "n_orders":
      return useHourlyTitle
        ? "Running Hourly Orders (as % of typical full day)"
        : "Orders (as % of typical full day)";
    case "avg_unit_price":
      return useHourlyTitle
        ? "Running Average Unit Price (as % of typical full day)"
        : "Average Unit Price (as % of typical full day)";
    default:
      return useHourlyTitle
        ? "Running Hourly Revenue (as % of typical full day)"
        : "Revenue (as % of typical full day)";
  }
};

/**
 * Generates Y-axis configuration with static ticks array based on actual data.
 * Computes min/max from data, rounds them to clean numbers, and generates evenly-spaced ticks.
 *
 * @param data - The chart data array
 * @param lineIds - Array of line IDs to check for min/max values
 * @param dataMinParam - Optional fixed minimum value (e.g., 0)
 * @param tickCount - Number of ticks to generate (default: 5)
 */
export const getYAxisConfig = (
  data: any[] | undefined,
  lineIds: string[],
  dataMinParam?: number,
  tickCount: number = 5
): {
  domain: [number, number];
  ticks: number[];
} => {
  // Default fallback
  if (!data || data.length === 0) {
    return {
      domain: [0, 10],
      ticks: [0, 2.5, 5, 7.5, 10],
    };
  }

  // Find actual min/max from data
  let dataMin = Infinity;
  let dataMax = -Infinity;

  data.forEach((point) => {
    lineIds.forEach((lineId) => {
      const value = point[lineId];
      if (typeof value === "number" && !isNaN(value)) {
        dataMin = Math.min(dataMin, value);
        dataMax = Math.max(dataMax, value);
      }
    });
  });

  // Handle edge cases
  if (dataMin === Infinity || dataMax === -Infinity) {
    return {
      domain: [0, 10],
      ticks: [0, 2.5, 5, 7.5, 10],
    };
  }

  // Rounding logic that handles values from 0.001 to millions
  // Uses the range between min and max to determine appropriate magnitude
  const getRoundingInterval = (min: number, max: number): number => {
    const range = max - min;

    // If we have a meaningful range, use it to determine magnitude
    if (range > 0.001) {
      const rangeMagnitude = Math.pow(10, Math.floor(Math.log10(range)));
      return rangeMagnitude / 2;
    }

    // Fallback to individual value-based logic for edge cases
    const value = max !== 0 ? max : min;
    if (value === 0) return 0.1;

    const magnitude = Math.pow(10, Math.floor(Math.log10(Math.abs(value))));

    // For all magnitudes, use half the magnitude as the rounding interval
    // This creates clean steps:
    // 0.001-0.01: round to 0.0005, 0.001, 0.0015...
    // 0.01-0.1: round to 0.005, 0.01, 0.015...
    // 0.1-1: round to 0.05, 0.1, 0.15...
    // 1-10: round to 0.5, 1, 1.5...
    // 10-100: round to 5, 10, 15...
    // 100-1K: round to 50, 100, 150...
    // And so on...
    return magnitude / 2;
  };

  // Round min/max
  const roundingInterval = getRoundingInterval(dataMin, dataMax);

  const roundedMin =
    dataMinParam !== undefined
      ? dataMinParam
      : Math.floor(dataMin / roundingInterval) * roundingInterval;

  const roundedMax =
    dataMax === 0
      ? 10
      : Math.ceil(dataMax / roundingInterval) * roundingInterval;

  // Generate evenly-spaced ticks
  const range = roundedMax - roundedMin;
  const step = range / (tickCount - 1);
  const ticks: number[] = [];

  for (let i = 0; i < tickCount; i++) {
    ticks.push(roundedMin + step * i);
  }

  return {
    domain: [roundedMin, roundedMax],
    ticks,
  };
};

// This help setting a default height for graphs when they are loading,
// preventing page jumps (Cumulative Layout Shift)
export const getBaseGraphPaperLoadingStyles = (
  isLoading: boolean,
  height: number
): { sx: SxProps } | undefined => {
  if (!isLoading) return;

  return {
    sx: {
      minHeight: height,
    },
  };
};
